import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  iconClassName?: string;
  iconBgClassName?: string;
  action?: React.ReactNode;
  compact?: boolean;
}

/**
 * Shared empty / blank state panel used across search pages, dashboard sections,
 * and any other place that needs a consistent "nothing here yet" treatment.
 *
 * - `compact` — smaller icon + less vertical padding; use inside cards/panels
 * - `iconBgClassName` — background colour for the icon circle (default slate-100)
 * - `iconClassName` — colour of the icon itself (default slate-400)
 * - `action` — optional CTA rendered below the description
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  iconClassName = "text-slate-400",
  iconBgClassName = "bg-slate-100",
  action,
  compact = false,
}: EmptyStateProps) {
  return (
    <div className={cn(
      "flex flex-col items-center justify-center text-center",
      compact ? "py-8 px-4" : "py-20 px-6",
    )}>
      <div className={cn(
        "rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm",
        compact ? "w-12 h-12" : "w-16 h-16",
        iconBgClassName,
      )}>
        <Icon className={cn(compact ? "w-5 h-5" : "w-7 h-7", iconClassName)} />
      </div>
      <h3 className={cn(
        "font-bold text-slate-800 mb-1.5",
        compact ? "text-sm" : "text-lg",
      )}>
        {title}
      </h3>
      <p className={cn(
        "text-slate-500 leading-relaxed max-w-xs mx-auto",
        compact ? "text-xs" : "text-sm",
      )}>
        {description}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
